import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { getMyLoginInfo, setMyLoginPassword } from '../../../shared/api/loginInfo';

const maskPhone = (p) => (p && p.length >= 7 ? `${p.slice(0, 3)}****${p.slice(-4)}` : p);
const PASSWORD_RE = /^[A-Za-z0-9]{6,20}$/;

/**
 * 登录信息 — what a family needs when the account moves from mum's phone to
 * grandma's: the phone number on the account and the password, each behind an
 * eye button. Authing never returns a password, so one set on the login page
 * shows as "not viewable" until the user sets a new one here (kept encrypted
 * server-side from then on). WeChat logins are told they need no password.
 */
export default function LoginInfoSection() {
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let alive = true;
    getMyLoginInfo().then((r) => { if (alive) setInfo(r); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const pw = draft.trim();
    if (!PASSWORD_RE.test(pw)) { setSaveError('密码要 6–20 位，只能用字母和数字'); return; }
    setSaving(true); setSaveError('');
    try {
      const r = await setMyLoginPassword(pw);
      setInfo((prev) => ({ ...prev, passwordKnown: true, password: r.password }));
      setEditing(false); setDraft(''); setShowPassword(true);
    } catch (err) {
      setSaveError(err.message || '没保存成功，再试一次');
    } finally {
      setSaving(false);
    }
  };

  if (failed) return null; // settings still works without this block
  if (!info) return <section className="settings-section settings-login" aria-busy="true"><div className="settings-login__head"><KeyRound size={18} className="settings-install-icon" /><span className="settings-install-label">登录信息</span></div></section>;

  const { phone, loginMethod, passwordKnown, password } = info;
  return (
    <section className="settings-section settings-login">
      <div className="settings-login__head">
        <KeyRound size={18} className="settings-install-icon" />
        <span className="settings-install-label">登录信息</span>
        <span className="settings-login__why">换手机时用</span>
      </div>

      {loginMethod === 'wechat' && (
        <p className="settings-login__note">这个账号是用<b>微信</b>登录的。换手机时直接用微信登录就行，不需要密码。</p>
      )}

      <div className="settings-login__row">
        <span className="settings-login__label">手机号</span>
        <b className="settings-login__value">{phone ? (showPhone ? phone : maskPhone(phone)) : <span className="settings-login__muted">没有绑定手机号</span>}</b>
        {phone && (
          <button type="button" className="settings-login__eye" aria-label={showPhone ? '隐藏手机号' : '显示手机号'} aria-pressed={showPhone} onClick={() => setShowPhone((v) => !v)}>
            {showPhone ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>

      {loginMethod === 'phone' && (passwordKnown && !editing ? (
        <div className="settings-login__row">
          <span className="settings-login__label">密码</span>
          <b className="settings-login__value">{showPassword ? password : '••••••••'}</b>
          <button type="button" className="settings-login__eye" aria-label={showPassword ? '隐藏密码' : '显示密码'} aria-pressed={showPassword} onClick={() => setShowPassword((v) => !v)}>
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          <button type="button" className="settings-login__link" onClick={() => { setEditing(true); setDraft(''); }}>换一个</button>
        </div>
      ) : editing ? (
        <form className="settings-login__edit" onSubmit={save}>
          <label className="settings-login__label" htmlFor="login-new-password">新密码</label>
          <input id="login-new-password" className="settings-login__input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="6–20 位字母或数字" autoComplete="new-password" inputMode="latin" />
          <div className="settings-login__actions">
            <button type="submit" className="settings-install-btn" disabled={saving}>{saving ? '保存中...' : '保存'}</button>
            <button type="button" className="settings-login__link" onClick={() => { setEditing(false); setSaveError(''); }}>取消</button>
          </div>
          {saveError && <p className="settings-login__error">{saveError}</p>}
        </form>
      ) : (
        <div className="settings-login__row">
          <span className="settings-login__label">密码</span>
          <span className="settings-login__muted">这里看不到（是在登录页设的）</span>
          <button type="button" className="settings-install-btn" onClick={() => { setEditing(true); setDraft(''); }}>设一个新密码</button>
        </div>
      ))}
    </section>
  );
}
